import { watch, nextTick, onBeforeUnmount, type Ref } from "vue";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

interface FocusTrapOptions {
  /** Called when Escape is pressed while the trap is active. */
  onEscape?: () => void;
  /**
   * "container" focuses the container itself on activation (it needs
   * tabindex="-1"); "first" focuses the first focusable element inside.
   */
  initialFocus?: "container" | "first";
}

/**
 * Traps Tab focus inside `containerRef` while `active` is true, closes on
 * Escape via `onEscape`, and restores focus to the previously focused
 * element on deactivation. Used by Modal and other overlays.
 */
export function useFocusTrap(
  containerRef: Ref<HTMLElement | null>,
  active: Ref<boolean>,
  options: FocusTrapOptions = {},
) {
  let previouslyFocused: HTMLElement | null = null;

  const getFocusable = (): HTMLElement[] => {
    if (!containerRef.value) return [];
    return Array.from(
      containerRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      options.onEscape?.();
      return;
    }

    if (event.key !== "Tab" || !containerRef.value) return;

    const focusable = getFocusable();
    if (focusable.length === 0) {
      event.preventDefault();
      containerRef.value.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement as HTMLElement | null;
    const inside = current !== null && containerRef.value.contains(current);

    if (event.shiftKey) {
      if (!inside || current === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (!inside || current === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const activate = async () => {
    previouslyFocused = document.activeElement as HTMLElement | null;
    // Bubble phase, so inner widgets (an open dropdown, a tooltip) can
    // handle Escape themselves and stop it from closing the whole overlay.
    document.addEventListener("keydown", handleKeydown);
    await nextTick();
    if (options.initialFocus === "first") {
      (getFocusable()[0] ?? containerRef.value)?.focus();
    } else {
      containerRef.value?.focus();
    }
  };

  const deactivate = () => {
    document.removeEventListener("keydown", handleKeydown);
    previouslyFocused?.focus();
    previouslyFocused = null;
  };

  watch(
    active,
    (isActive) => {
      if (isActive) {
        activate();
      } else {
        deactivate();
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", handleKeydown);
  });
}
